import {SvgIcon, SvgIconProps} from '@ui/icons/svg-icon';
import React, {ComponentType, ReactElement, RefObject} from 'react';

export function createSvgIcon(
  path: ReactElement | ReactElement[],
  displayName: string = '',
  viewBox?: string,
): ComponentType<SvgIconProps> {
  const Component = (props: SvgIconProps, ref: RefObject<SVGSVGElement>) => (
    <SvgIcon
      data-testid={`${displayName}Icon`}
      ref={ref}
      viewBox={viewBox}
      {...props}
      size={props.size || 'md'}
    >
      {path}
    </SvgIcon>
  );

  if (process.env.NODE_ENV !== 'production') {
    // Need to set `displayName` on the inner component for React.memo.
    // React prior to 16.14 ignores `displayName` on the wrapper.
    Component.displayName = `${displayName}Icon`;
  }

  const wrapped = React.memo(React.forwardRef(Component as any));
  // Stash a stable fingerprint of the icon's path data + its display name so a
  // stored IconTree (which keeps only the `<path d=…>` children, not the name)
  // can be reverse-mapped back to a human-readable name — e.g. for a tooltip in
  // the plan feature-icon picker. See `icon-name-lookup.ts`.
  (wrapped as IconComponentWithMeta).iconPathData = pathFingerprint(path);
  (wrapped as IconComponentWithMeta).iconDisplayName = displayName;
  return wrapped;
}

export interface IconComponentWithMeta {
  iconPathData?: string;
  iconDisplayName?: string;
}

/**
 * Join the `d` attribute of every `<path>` in the icon's source JSX into one
 * string — the same data that survives into a stored IconTree, so it works as
 * a lookup key. Icons without plain `<path d>` children (rare) get an empty
 * fingerprint and are simply skipped by the reverse lookup.
 */
function pathFingerprint(path: ReactElement | ReactElement[]): string {
  const nodes = Array.isArray(path) ? path : [path];
  return nodes
    .map(node => (node?.props as {d?: string} | undefined)?.d ?? '')
    .join('|');
}

export interface IconTree {
  tag: string;
  attr?: {[key: string]: string};
  // Can't use "IconTree", otherwise there's circular reference error in hook form
  child?: {tag: string; attr?: {[key: string]: string}}[];
}
export function createSvgIconFromTree(
  data: IconTree[],
  displayName: string = '',
) {
  const path = treeToElement(data);
  return path ? createSvgIcon(path, displayName) : null;
}

function treeToElement(
  tree?: IconTree[],
): React.ReactElement<{}>[] | undefined {
  return (
    tree?.map &&
    tree.map((node, i) => {
      return React.createElement(
        node.tag,
        {key: i, ...node.attr},
        treeToElement(node.child),
      );
    })
  );
}

export function elementToTree(el: HTMLElement | SVGElement): IconTree {
  const attributes: IconTree['attr'] = {};
  const tree: IconTree = {tag: el.tagName, attr: attributes};
  Array.from(el.attributes).forEach(attribute => {
    attributes[attribute.name] = attribute.value;
  });
  if (el.children.length) {
    tree.child = Array.from(el.children).map(child =>
      elementToTree(child as HTMLElement),
    );
  }
  return tree;
}
